import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Row } from 'react-styled-flexboxgrid';
import Image from '../../../../components/Image';
import Paper from '../../../../components/Paper';
import Rating from '../../../../components/Rating';
import Select from '../../../../components/Select';
import { findByID, getImageUrl, parseDataToOptions } from '../../../../utils/helpers';
import { setLoadingPlaces, setPlaces, setSelectedPlace } from '../../../../redux/places/actions';
import { makeStyles } from '../../../../utils/styles';
import useTheme from '../../../../hooks/useTheme';
import { useApi } from '../../../../hooks/useApi';
import { ApiPlaces } from '../../../../api/places';
import { ROLES } from '../../../../utils/constans';
import Typography from '../../../../components/Typography';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles(css => ({
    paper: css`
        display: inline-flex;
        align-items: center;
        height: 40px;
        padding: 5px 15px;
        border-radius: 20px;
    `,
    select: css`
        margin-bottom: 0;
    `
}));

const PlaceSelect = () => {
    const { t } = useTranslation()
    const styles = useStyles();
    const theme = useTheme();
    const dispatch = useDispatch();

    const user = useSelector(state => state.auth?.user);
    const role = useSelector(state => state.auth?.user?.user?.role?.type);
    const userSupplierPlaceID = useSelector(state => state.auth?.user?.user?.user_supplier?.place_id);
    const placeSelected = useSelector(state => state.places.selected);

    const [{ data: places, loading }, getPlaces] = useApi(ApiPlaces.get, { manual: true });

    useEffect(() => {
        dispatch(setLoadingPlaces(loading));
    }, [loading, dispatch]);

    useEffect(() => {
        dispatch(setPlaces(places));
    }, [places, dispatch]);

    // Memoize data
    const placesParsed = useMemo(
        () => {
            if (!!!places) return [];
            return role === ROLES.SUPERADMIN ? [{ name: t('Pokaż wszystkie'), id: 'all' }, ...places] : places;
        },
        [places, role, t]
    );

    const placesOptions = useMemo(
        () => placesParsed && parseDataToOptions(placesParsed),
        [placesParsed]
    );

    useEffect(() => {
        if (role === ROLES.SUPERADMIN) {
            getPlaces();
        } else if (userSupplierPlaceID !== undefined && userSupplierPlaceID !== null) {
            getPlaces({ id: userSupplierPlaceID });
        }
    }, [role, userSupplierPlaceID, getPlaces])

    useEffect(() => {
        if (role !== ROLES.SUPERADMIN) {
            const place = placesParsed && findByID(placesParsed, user?.user?.user_supplier?.place_id);
            if (!!!placeSelected && placeSelected?.id !== place?.id) {
                dispatch(setSelectedPlace(place));
            }
        } else if (placesParsed && placesParsed.length > 0) {
            // Is exist a current place in places
            const place = (placesParsed && placeSelected) && placesParsed.find(place => place.id === placeSelected.id);
            if(!!!placeSelected || !!!place) {
                dispatch(setSelectedPlace(placesParsed[0]));
            }
        }
    }, [user, role, placesParsed, placeSelected, dispatch]);

    const handleChangePlace = e => {
        if (!!!placesParsed || role !== ROLES.SUPERADMIN) return;
        const nextPlace = findByID(placesParsed, e.target.value);
        dispatch(setSelectedPlace(nextPlace));
    };

    return (
        <Row between="xs" middle="xs">
            <Col>
                {
                    placesParsed?.length > 0 ?
                        <Row between="xs" middle="xs">
                            <Col>
                                {
                                    placeSelected &&
                                    <Image
                                        src={getImageUrl(placeSelected?.logo?.url)}
                                        alt=""
                                        maxHeight="40px"
                                    />
                                }
                            </Col>
                            <Col>
                                {
                                    role === ROLES.SUPERADMIN &&
                                    <Select
                                        placeholder={t("Wybierz")}
                                        value={placeSelected?.id ?? ""}
                                        options={placesOptions}
                                        customStyle={styles.select}
                                        noBorder
                                        font={theme.fonts.mulishRegular}
                                        onChange={handleChangePlace}
                                        disabled={role !== ROLES.SUPERADMIN}
                                    />
                                }
                                {
                                    (role !== ROLES.SUPERADMIN && placeSelected?.name) &&
                                    <Typography
                                        font={theme.fonts.mulishRegular}
                                        as="h2"
                                    >
                                        {placeSelected?.name}
                                    </Typography>
                                }
                            </Col>
                        </Row>
                        :
                        <Typography
                            font={theme.fonts.mulishRegular}
                            as="h2"
                        >
                            {
                                loading ?
                                    t("Ładowanie")
                                    :
                                    role === ROLES.SUPERADMIN ? t('Brak obiektów') : t('Brak przypisanego obiektu')
                            }
                        </Typography>
                }

            </Col>
            <Col>
                {
                    (placeSelected && placeSelected?.id !== 'all') &&
                    <Paper
                        inline
                        customStyle={styles.paper}
                    >
                        <Rating rate={placeSelected?.rankings} />
                    </Paper>
                }
            </Col>
        </Row>
    )
}

export default PlaceSelect;
